import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  IconButton,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import writeXlsxFile from 'write-excel-file';
import LongStringView from '../Shared/LongStringView/index.tsx';

const truncateCharacters = (text: string, charLimit: number = 50): string => {
  if (!text) return '';
  return text.length <= charLimit ? text : text.slice(0, charLimit) + '...';
};

const Benchmarking: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();
  const [searchParams] = useSearchParams();
  // const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [benchmarkingList, setBenchmarkingList] = useState<any[]>([]);

  const conversation_id = searchParams.get('chat_id') ?? '';
  const project_id = searchParams.get('project_id') ?? '';

  // const toggleRowExpand = (id: string) => {
  //   setExpandedRows((prev) => {
  //     const newSet = new Set(prev);
  //     newSet.has(id) ? newSet.delete(id) : newSet.add(id);
  //     return newSet;
  //   });
  // };

  const fetchBenchmarkData = async () => {
    try {
      const res = await aiActions.getBenchmarkReport({
        conversation_id,
        project_id,
      });
      const formatted = formatBenchmarkData(res?.data);
      setBenchmarkingList(formatted);
    } catch (err) {
      showSnackBar('Failed to load Benchmark Report', 'error');
    }
  };

  useEffect(() => {
    fetchBenchmarkData();
  }, []);

  const formatHeader = (key: string) => {
  const map: Record<string, string> = {
    mdai: 'MD AI',
    chatgpt: 'ChatGPT',
    gemini: 'Gemini',
    claude: 'Claude',
    grok: 'Grok',
    metaai: 'Meta AI',
    perplexity: 'Perplexity',
    deepseek: 'DeepSeek',
  };
  return map[key.toLowerCase()] || key;
};


  const formatBenchmarkData = (apiData: any) => {
    const formatted: any[] = [];
    const modelKeys = Object.keys(apiData || {});
    const allConversations = new Set<string>();

    modelKeys.forEach((model) => {
      Object.keys(apiData[model] || {}).forEach((cid) => {
        allConversations.add(cid);
      });
    });

    Array.from(allConversations).forEach((cid, index) => {
      const row: any = { id: String(index + 1), query: '' };
      modelKeys.forEach((model) => {
        const item = apiData[model]?.[cid]?.[0];
        if (item) {
          row.query = item.query || row.query;
          row[model] = typeof item.response === 'object'
  ? JSON.stringify(item.response, null, 2)
  : item.response;

        }
      });
      formatted.push(row);
    });

    return formatted;
  };

  const renderCell = (field: string, width = '200px') => ({
  accessorKey: field,
  header: formatHeader(field),
  size: 200,
  Cell: ({ row }: any) => {
    const content = row.original[field];
    return (
      <Box sx={{ maxWidth: '100%' }}>
        <LongStringView longString={typeof content === 'object' ? JSON.stringify(content, null, 2) : content} />
      </Box>
    );
  },
});


  const columns = useMemo(() => {
    if (!benchmarkingList.length) return [];
    const sample = benchmarkingList[0];
    const dynamicKeys = Object.keys(sample).filter(
      (key) => key !== 'id' && key !== 'query'
    );
    return [
      { accessorKey: 'id', header: 'Sr No.', size: 60, enableSorting: false,  },
      { accessorKey: 'query', header: 'Query', size: 200, enableSorting: false, },
      ...dynamicKeys.map((key) => renderCell(key)),
    ];
  }, [benchmarkingList]);

  const downloadXLSX = async () => {
  if (!benchmarkingList.length) return;

  const sample = benchmarkingList[0];
  const keys = Object.keys(sample);

  const schema = keys.map((key) => ({
    column: key.toUpperCase(),
    type: String, // This tells write-excel-file to infer "string" type properly
    value: (row: any) => row[key] ?? '',
  }));

  await writeXlsxFile(benchmarkingList, {
    schema,
    fileName: 'benchmarking-report.xlsx',
  });
};

  return (
    <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <DynamicBreadcrumbs
        items={[
          {
            label: (
              <IconButton color="primary">
                <HomeIcon />
              </IconButton>
            ),
            href: '/',
          },
          { label: 'AI', onClick: () => navigate('/r/ai') },
          { label: 'Benchmarking Report' },
        ]}
        onBreadcrumbClick={(e, item) => item?.onClick?.()}
      />

      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={6}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Benchmarking Report
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Button variant="contained" size="small" onClick={downloadXLSX}>
            Download as XLSX
          </Button>
        </Grid>
      </Grid>

      <CommonTable
        columns={columns}
        data={benchmarkingList}
        enablePagination={false}
        enableColumnFilters={false}
        enableStickyHeader={true}
        enableTopToolbar={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnActions={false}
        enableHiding={false}
        isLoading={false}
      />
    </Box>
  );
};

export default Benchmarking;
